/**
 * PURE model behind components/ConditionBuilder.jsx.
 *
 * The visual builder edits an approval step's trigger `condition` as "OR of AND groups":
 * one or more rule GROUPS, each group a list of field conditions AND-ed together, the
 * groups themselves OR-ed. That is exactly the two-level shape lib/conditions.js already
 * evaluates (`{ any: [ { all: [...] }, ... ] }`), so no engine change is involved.
 *
 * Deliberately two levels only (AND within OR). Arbitrary nesting, `not` around a whole
 * group, mixed `any` inside `all`, etc. are NOT represented here — `parseCondition`
 * returns null for those and the component falls back to its raw JSON editor, the same
 * "unsupported shape falls back to raw" rule the previous single-field builder used.
 *
 * Serialization is kept as flat as the shape allows, so simple conditions authored before
 * this existed round-trip byte-identically and audit logs stay readable:
 *   one group, one condition  -> a bare leaf   { path, op, value }
 *   one group, many           -> { all: [...] }
 *   many groups               -> { any: [ { all: [...] } | leaf, ... ] }
 *
 * Split out of the component (rather than living inside it) because web/'s test runner is
 * plain `node --test` over src/lib/*.test.js — no JSX transform — so the logic that
 * actually needs pinning lives here and is unit-tested directly.
 */

/**
 * Fields an admin can branch approval steps on. Add here as new matter fields become
 * useful branch points — no engine change needed, conditions already support any dotted
 * path on the matter document.
 *
 * kind:
 *   'single'  — matter stores one id       (ops: in / nin, multi-select of option ids)
 *   'multi'   — matter stores an array     (ops: contains_any / none-of, multi-select)
 *   'boolean' — matter stores a boolean    (ops: is_true / is_false, no options list)
 */
export const BUILDER_FIELDS = [
  {
    path: 'header.practiceGroupId',
    label: 'Practice Group',
    kind: 'single',
    optionsFrom: 'practiceGroups',
  },
  {
    path: 'areasOfLaw',
    label: 'Area of Law',
    kind: 'multi',
    optionsFrom: 'areasOfLaw',
  },
  {
    path: 'header.isNewClient',
    label: 'New or existing client',
    kind: 'boolean',
    optionsFrom: null,
  },
  {
    path: 'matterType.isLitigation',
    label: 'Litigation matter',
    kind: 'boolean',
    optionsFrom: null,
  },
];

export const SINGLE_OPS = [
  ['in', 'is one of'],
  ['nin', 'is none of'],
];
export const MULTI_OPS = [
  ['contains_any', 'includes any of'],
  ['not', 'includes none of'], // wrapped as { not: { path, op: contains_any, value } }
];
export const BOOLEAN_OPS = [
  ['is_true', 'is Yes'],
  ['is_false', 'is No'],
];
// "Has this even been answered yet" — distinct from matching a specific value. The
// leading case is a dropdown still sitting on its unselected "Select…" placeholder
// (stored as an empty string), so a later field can stay hidden until a prior one is
// actually touched, not just until it happens to equal something. Appended (not merged
// into) SINGLE_OPS/MULTI_OPS so existing op lists/defaults are unchanged; not offered for
// 'boolean' fields — see CLAUDE.md's is_false-not-falsy rule: an unanswered yesno is
// deliberately never treated as a branch point here, and exposing exists/not_exists on a
// boolean would just reopen that exact "unanswered manufactures a branch" mistake.
export const EXISTENCE_OPS = [
  ['exists', 'has been answered'],
  ['not_exists', 'has not been answered yet'],
];

/** Human-readable wording for the two boolean fields, which read badly as "is Yes". */
const BOOLEAN_OP_LABELS = {
  'header.isNewClient': { is_true: 'is a NEW client', is_false: 'is an EXISTING client' },
  'matterType.isLitigation': { is_true: 'is litigation', is_false: 'is not litigation' },
};

export function fieldByPath(path, fields = BUILDER_FIELDS) {
  return fields.find((f) => f.path === path) || null;
}

export function opsFor(fieldDef) {
  if (!fieldDef) return [...SINGLE_OPS, ...EXISTENCE_OPS];
  if (fieldDef.kind === 'boolean') return BOOLEAN_OPS;
  if (fieldDef.kind === 'multi') return [...MULTI_OPS, ...EXISTENCE_OPS];
  return [...SINGLE_OPS, ...EXISTENCE_OPS];
}

export function opLabel(fieldDef, op) {
  const override = fieldDef && BOOLEAN_OP_LABELS[fieldDef.path];
  if (override && override[op]) return override[op];
  const found = opsFor(fieldDef).find(([o]) => o === op);
  return found ? found[1] : op;
}

export function defaultOpFor(fieldDef) {
  return opsFor(fieldDef)[0][0];
}

/** A fresh, empty condition row for a field (defaults to the first configured field). */
export function newCondition(path, fields = BUILDER_FIELDS) {
  const first = fields[0] || BUILDER_FIELDS[0];
  const fieldDef = fieldByPath(path || first.path, fields) || first;
  return { path: fieldDef.path, op: defaultOpFor(fieldDef), values: [] };
}

export function newGroup(fields = BUILDER_FIELDS) {
  return { conditions: [newCondition(undefined, fields)] };
}

/** True once a row is complete enough to serialize (booleans and exists/not_exists need no values). */
export function isConditionComplete(cond, fields = BUILDER_FIELDS) {
  const fieldDef = fieldByPath(cond && cond.path, fields);
  if (!fieldDef) return false;
  if (fieldDef.kind === 'boolean') return cond.op === 'is_true' || cond.op === 'is_false';
  if (cond.op === 'exists' || cond.op === 'not_exists') return true;
  return Array.isArray(cond.values) && cond.values.length > 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Parsing: condition JSON -> groups
// ─────────────────────────────────────────────────────────────────────────────

/** One leaf condition -> { path, op, values }, or null when not builder-representable. */
function parseLeaf(node, fields) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return null;

  // { not: { path, op: 'contains_any', value } } — the "includes none of" multi op.
  if (node.not) {
    const inner = node.not;
    const fieldDef = fieldByPath(inner && inner.path, fields);
    if (!fieldDef || fieldDef.kind !== 'multi' || inner.op !== 'contains_any') return null;
    if (Object.keys(node).length !== 1) return null;
    return { path: fieldDef.path, op: 'not', values: toValues(inner.value) };
  }

  if (node.all || node.any) return null;

  const fieldDef = fieldByPath(node.path, fields);
  if (!fieldDef) return null;

  if (fieldDef.kind === 'boolean') {
    if (node.op !== 'is_true' && node.op !== 'is_false') return null;
    return { path: fieldDef.path, op: node.op, values: [] };
  }
  if (node.op === 'exists' || node.op === 'not_exists') {
    return { path: fieldDef.path, op: node.op, values: [] };
  }
  if (fieldDef.kind === 'single') {
    if (!['in', 'nin', 'eq', 'neq'].includes(node.op)) return null;
    // eq/neq predate the builder; they are the single-value form of in/nin.
    const op = node.op === 'eq' ? 'in' : node.op === 'neq' ? 'nin' : node.op;
    return { path: fieldDef.path, op, values: toValues(node.value) };
  }
  // multi
  if (!['contains_any', 'contains'].includes(node.op)) return null;
  return { path: fieldDef.path, op: 'contains_any', values: toValues(node.value) };
}

function toValues(value) {
  if (Array.isArray(value)) return [...value];
  if (value === undefined || value === null) return [];
  return [value];
}

/** A group: either an `all` of leaves, or a single bare leaf. */
function parseGroup(node, fields) {
  if (node && typeof node === 'object' && Array.isArray(node.all)) {
    const conditions = node.all.map((leaf) => parseLeaf(leaf, fields));
    if (conditions.length === 0 || conditions.some((c) => c === null)) return null;
    return { conditions };
  }
  const leaf = parseLeaf(node, fields);
  return leaf ? { conditions: [leaf] } : null;
}

/**
 * @returns {{ groups: Array<{conditions: Array}> }|null}
 *   null = shape the builder cannot represent; the caller should use the raw JSON editor.
 *   An "always applies" condition (true / null / undefined) parses to one empty group.
 */
export function parseCondition(condition, fields = BUILDER_FIELDS) {
  if (condition === true || condition === null || condition === undefined) {
    return { groups: [{ conditions: [] }] };
  }
  if (typeof condition !== 'object' || Array.isArray(condition)) return null;

  if (Array.isArray(condition.any)) {
    if (Object.keys(condition).length !== 1 || condition.any.length === 0) return null;
    const groups = condition.any.map((group) => parseGroup(group, fields));
    if (groups.some((g) => g === null)) return null;
    return { groups };
  }

  const group = parseGroup(condition, fields);
  return group ? { groups: [group] } : null;
}

export function isBuilderCondition(condition, fields = BUILDER_FIELDS) {
  return parseCondition(condition, fields) !== null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Serializing: groups -> condition JSON
// ─────────────────────────────────────────────────────────────────────────────

function serializeLeaf(cond, fields) {
  const fieldDef = fieldByPath(cond.path, fields);
  if (fieldDef.kind === 'boolean') return { path: cond.path, op: cond.op };
  if (cond.op === 'exists' || cond.op === 'not_exists') return { path: cond.path, op: cond.op };
  if (cond.op === 'not') {
    return { not: { path: cond.path, op: 'contains_any', value: [...cond.values] } };
  }
  return { path: cond.path, op: cond.op, value: [...cond.values] };
}

/**
 * Groups -> the flattest equivalent condition JSON. Incomplete rows (a field with no
 * value picked yet) are dropped, and a builder with nothing complete in it serializes to
 * `true` — "this step always applies", the same default the previous builder emitted.
 */
export function serializeGroups(groups, fields = BUILDER_FIELDS) {
  const serialized = (groups || [])
    .map((g) => (g.conditions || []).filter((condition) => isConditionComplete(condition, fields)).map((condition) => serializeLeaf(condition, fields)))
    .filter((leaves) => leaves.length > 0)
    .map((leaves) => (leaves.length === 1 ? leaves[0] : { all: leaves }));

  if (serialized.length === 0) return true;
  if (serialized.length === 1) return serialized[0];
  return { any: serialized };
}

// ─────────────────────────────────────────────────────────────────────────────
// Immutable group/condition edits
// ─────────────────────────────────────────────────────────────────────────────

const mapGroup = (groups, gi, fn) => groups.map((g, i) => (i === gi ? fn(g) : g));

export const addGroup = (groups, fields = BUILDER_FIELDS) => [...groups, newGroup(fields)];

/** Removing the last group leaves one empty group, i.e. "always applies". */
export const removeGroup = (groups, gi) => {
  const next = groups.filter((_, i) => i !== gi);
  return next.length > 0 ? next : [{ conditions: [] }];
};

export const addCondition = (groups, gi, fields = BUILDER_FIELDS) =>
  mapGroup(groups, gi, (g) => ({ ...g, conditions: [...(g.conditions || []), newCondition(undefined, fields)] }));

/** Removing the last condition in a group removes the group, unless it is the only one. */
export const removeCondition = (groups, gi, ci) => {
  const group = groups[gi];
  const conditions = (group.conditions || []).filter((_, i) => i !== ci);
  if (conditions.length === 0 && groups.length > 1) return removeGroup(groups, gi);
  return mapGroup(groups, gi, (g) => ({ ...g, conditions }));
};

/** Changing a row's field resets its op/values, since neither is meaningful across kinds. */
export const setConditionField = (groups, gi, ci, path, fields = BUILDER_FIELDS) =>
  mapGroup(groups, gi, (g) => ({
    ...g,
    conditions: g.conditions.map((c, i) => (i === ci ? newCondition(path, fields) : c)),
  }));

export const setConditionOp = (groups, gi, ci, op) =>
  mapGroup(groups, gi, (g) => ({
    ...g,
    conditions: g.conditions.map((c, i) => (i === ci ? { ...c, op } : c)),
  }));

export const toggleConditionValue = (groups, gi, ci, id) =>
  mapGroup(groups, gi, (g) => ({
    ...g,
    conditions: g.conditions.map((c, i) =>
      i === ci
        ? {
            ...c,
            values: c.values.includes(id)
              ? c.values.filter((v) => v !== id)
              : [...c.values, id],
          }
        : c
    ),
  }));

/** Replaces a condition's values (used by free-text field targets). */
export const setConditionValues = (groups, gi, ci, values) =>
  mapGroup(groups, gi, (g) => ({
    ...g,
    conditions: g.conditions.map((c, i) => (i === ci ? { ...c, values } : c)),
  }));
